import React, { useState, useEffect, useRef } from 'react';
import './YouTubeSearch.css';
import config from '../config';

interface YouTubeVideo {
  id: string;
  title: string;
  thumbnail: string;
  channelTitle: string;
}

interface YouTubeSearchProps {
  onVideoSelect: (videoId: string) => void;
}

const YouTubeSearch: React.FC<YouTubeSearchProps> = ({ onVideoSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimeout = useRef<number | null>(null);

  // Efecto para realizar búsqueda en tiempo real
  useEffect(() => {
    // Limpiar el timeout anterior
    if (searchTimeout.current) {
      window.clearTimeout(searchTimeout.current);
    }

    if (!searchTerm.trim()) {
      return;
    }

    // Establecer un pequeño retraso para evitar demasiadas solicitudes
    searchTimeout.current = window.setTimeout(() => {
      performSearch(searchTerm);
    }, 500); // Esperar 500ms después de que el usuario deje de escribir

    return () => {
      if (searchTimeout.current) {
        window.clearTimeout(searchTimeout.current);
      }
    };
  }, [searchTerm]);

  // Función para crear una señal de aborto con timeout
  const createAbortSignalWithTimeout = (timeoutMs: number): AbortSignal => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    
    // Cleanup function to clear the timeout
    controller.signal.addEventListener('abort', () => {
      window.clearTimeout(timeout);
    });
    
    return controller.signal;
  };

  const performSearch = async (term: string) => {
    if (!term.trim()) return;

    setLoading(true);
    setError(null);

    try {
      console.log(`Realizando búsqueda usando base URL: ${config.apiUrl}`);
      
      // Verificar primero si el servidor está disponible
      try {
        console.log('Verificando estado del servidor...');
        const serverCheckResponse = await fetch(`${config.apiUrl}/api/status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          mode: 'cors',
          cache: 'no-cache'
        });
        
        console.log('Respuesta del servidor:', serverCheckResponse.status, serverCheckResponse.statusText);
        
        if (!serverCheckResponse.ok) {
          throw new Error(`Error al verificar el estado del servidor: ${serverCheckResponse.status}`);
        }
        
        const serverData = await serverCheckResponse.json();
        console.log('Estado del servidor:', serverData);
      } catch (error) {
        console.error('Error al verificar el estado del servidor:', error);
        
        // Intentar sin credenciales si hay error CORS
        try {
          console.log('Reintentando sin credenciales...');
          const retryResponse = await fetch(`${config.apiUrl}/api/status`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'omit', // No enviar cookies
            mode: 'cors',
            cache: 'no-cache'
          });
          
          if (!retryResponse.ok) {
            throw new Error(`Error al verificar el estado del servidor: ${retryResponse.status}`);
          }
          
          const serverData = await retryResponse.json();
          console.log('Estado del servidor (sin credenciales):', serverData);
        } catch (retryError) {
          console.error('Error al reintentar sin credenciales:', retryError);
          throw new Error('No se pudo conectar con el servidor. Verifica tu conexión.');
        }
      }
      
      // Realizar la búsqueda de YouTube
      console.log('Buscando videos en YouTube...');
      const response = await fetch(`${config.apiUrl}/api/youtube/search?q=${encodeURIComponent(term)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit', // Cambiar a omit para evitar problemas CORS
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en la respuesta del servidor:', errorText);
        throw new Error(`Error del servidor: ${response.status} ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Respuesta no válida (no es JSON):', text.substring(0, 200));
        throw new Error('El servidor no devolvió una respuesta JSON válida');
      }
      
      const data = await response.json();
      
      // Manejar diferentes formatos de respuesta posibles
      let processedVideos: YouTubeVideo[] = [];
      
      if (data.items && Array.isArray(data.items)) {
        processedVideos = data.items.map((item: any) => {
          // Comprobar si es el formato esperado o si es el formato de la API directa de YouTube
          if (item.id && typeof item.id === 'string') {
            // Nuestro formato actual
            return item as YouTubeVideo;
          } else if (item.id && item.id.videoId && item.snippet) {
            // Formato directo de la API de YouTube
            return {
              id: item.id.videoId,
              title: item.snippet.title,
              thumbnail: item.snippet.thumbnails?.medium?.url || '',
              channelTitle: item.snippet.channelTitle || ''
            };
          } else {
            console.warn('Formato de item desconocido:', item);
            return null;
          }
        }).filter(Boolean) as YouTubeVideo[];
      } else if (Array.isArray(data)) {
        // En caso de que la respuesta sea un array directamente
        processedVideos = data
          .filter((item: any) => item && (item.id || (item.id && item.id.videoId)))
          .map((item: any) => {
            if (item.id && typeof item.id === 'string') {
              return item as YouTubeVideo;
            } else {
              return {
                id: item.id.videoId,
                title: item.snippet.title,
                thumbnail: item.snippet.thumbnails?.medium?.url || '',
                channelTitle: item.snippet.channelTitle || ''
              };
            }
          });
      }
      
      setVideos(processedVideos);
    } catch (err: any) {
      console.error('Error buscando videos:', err);
      if (err.name === 'AbortError') {
        setError('La búsqueda tardó demasiado tiempo. Intente nuevamente.');
      } else {
        setError(`Error al buscar videos: ${err.message || 'Error desconocido'}`);
      }
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (video: YouTubeVideo) => {
    // Formatear el ID del video como una URL de YouTube
    const youtubeUrl = `https://www.youtube.com/watch?v=${video.id}`;
    onVideoSelect(youtubeUrl);
    
    // Scroll suave hacia arriba para mostrar el reproductor
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      performSearch(searchTerm);
    }
  };

  return (
    <div className="youtube-search-container">
      {/* Campo de búsqueda */}
      <form onSubmit={handleFormSubmit} className="relative group mb-4">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500/30 to-orange-500/30 rounded-xl blur opacity-0 group-focus-within:opacity-100 transition duration-300"></div>
        <div className="relative flex">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar videos de YouTube..."
            className="w-full p-3 rounded-l-lg bg-gray-800/90 border border-gray-700/50 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-red-500/40 transition-all duration-300"
          />
          <button 
            type="submit" 
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-r-lg transition-colors duration-300 flex items-center"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </form>

      {/* Mensajes de error */}
      {error && (
        <div className="mb-4 text-red-500 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p>{error}</p>
          <p className="text-xs mt-1">Consejo: Asegúrate de que el servidor esté en ejecución con 'npm run server'</p>
        </div>
      )}

      {/* Lista de resultados con altura fija para evitar estirar el contenedor */}
      <div className="youtube-search-results-wrapper" style={{ maxHeight: "350px", overflow: "hidden" }}>
        {loading ? (
          <div className="youtube-search-loading flex flex-col items-center justify-center py-10 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-red-500 mb-3"></div>
            <p>Buscando videos...</p>
          </div>
        ) : videos.length > 0 ? (
          <div className="youtube-search-results">
            {videos.map((video) => (
              <div 
                key={video.id} 
                className="youtube-search-item" 
                onClick={() => handleVideoSelect(video)}
              >
                <div className="relative">
                  <img 
                    src={video.thumbnail} 
                    alt={video.title} 
                    className="youtube-search-thumbnail"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 bg-black/50">
                    <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
                <h3 className="youtube-search-title">{video.title}</h3>
                <p className="youtube-search-channel">{video.channelTitle}</p>
              </div>
            ))}
          </div>
        ) : searchTerm ? (
          <div className="text-center p-10 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p>No se encontraron videos. Intenta con otra búsqueda.</p>
          </div>
        ) : (
          <div className="text-center p-10 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>Busca videos de YouTube para reproducir</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default YouTubeSearch; 